import { Dropdown } from '@/components/dropdown/dropdown'
import { NumberInput } from '@/components/number-input/numberInput'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { ToggleGroup } from '@/components/toggle-group/toggleGroup'
import { Typography } from '@/components/typography/typography'
import { WithLabel } from '@/components/with-label/with-label'
import { validateRoomConfiguration } from '@/features/room-config'
import { useDevice } from '@/hooks/useDevice'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { translate } from '@/utils/translator'

import type { RoomConfigurationType } from '../types'

const CHILDREN_AGE_RANGE_OPTIONS = [...Array(14).keys()].map((i) => ({
	value: String(i),
	label: String(i),
}))

const CHILDREN_DEFAULT_AGE = 7

type RoomConfigurationProps = {
	availableTypes: string[]
	roomIndex: number
	onDelete: (id: string) => void
	onUpdate: (roomConfiguration: RoomConfigurationType) => void
	roomConf: RoomConfigurationType
}

export const RoomConfiguration = ({
	availableTypes,
	roomIndex,
	onDelete,
	onUpdate,
	roomConf,
}: RoomConfigurationProps) => {
	const location = useCartStore.use.location()
	const { isDesktop } = useDevice()

	if (!location) return null
	const updateWithValidation = (roomConfiguration: RoomConfigurationType) => {
		const isValid = validateRoomConfiguration(
			roomConfiguration.adults,
			roomConfiguration.children,
			location.roomTypes
		)
		onUpdate({ ...roomConfiguration, isValid })
	}

	const handleChildrenUpdate = (value: number) =>
		updateWithValidation({
			...roomConf,
			children:
				value > roomConf.children.length
					? [...roomConf.children, CHILDREN_DEFAULT_AGE]
					: roomConf.children.slice(0, value),
		})

	const handleChildrenAgeUpdate = (value: string, index: number) => {
		const updatedRoom = {
			...roomConf,
		}
		updatedRoom.children[index] = Number(value)
		updateWithValidation(updatedRoom)
	}

	const handleTypeUpdate = (type?: string) => onUpdate({ ...roomConf, type: type })

	return (
		<article
			className={cn(
				'flex w-full flex-col gap-24 border-t border-textColor bg-primary pt-24 text-textColor',
				'md:rounded-8 md:border-none md:bg-roomConfiguration-background md:p-24 md:pb-32 md:text-roomConfiguration-text'
			)}
		>
			<div className={cn('flex flex-col gap-24 pb-16', 'md:gap-32 md:pb-8')}>
				<div className={cn('flex justify-between')}>
					<Typography
						style="h3"
						element="h3"
					>{`${translate('room-config-room')} ${roomIndex}`}</Typography>
					<button
						data-testid={`delete-room-conf-${roomIndex}`}
						onClick={() => onDelete(roomConf.id)}
					>
						<SvgIcon iconName="rounded-cross" className="h-24 w-24 text-current" />
					</button>
				</div>

				{availableTypes.length > 1 && (
					<ToggleGroup
						description="room type"
						value={roomConf.type}
						items={availableTypes}
						onSelect={handleTypeUpdate}
						direction={isDesktop ? 'vertical' : 'horizontal'}
						style={isDesktop ? 'inverted' : 'default'}
						alignment="left"
					/>
				)}
			</div>

			<Typography style="p-bold" element="h4">
				{translate('room-config-amount-of-persons')}
			</Typography>
			<WithLabel label={translate('room-config-adults')} data-testId="test">
				<NumberInput
					min={1}
					max={location?.maxAdults ?? 5}
					value={roomConf.adults}
					onUpdate={(value) => updateWithValidation({ ...roomConf, adults: value })}
				/>
			</WithLabel>
			{location?.childrenAllowed && (
				<WithLabel label={translate('room-config-children')}>
					<NumberInput
						min={0}
						max={location?.maxChildren ?? 2}
						value={roomConf.children.length}
						onUpdate={handleChildrenUpdate}
					/>
				</WithLabel>
			)}
			{roomConf.children.length > 0 && (
				<>
					<Typography style="p-bold" element="h4">
						{translate('room-config-children-age-departure')}
					</Typography>
					{roomConf.children.map((childAge, index) => (
						<WithLabel
							key={index}
							label={`${translate('room-config-child')} ${index + 1}: `}
							asLabel
						>
							<Dropdown
								placeholder={translate('room-config-children-placeholder')}
								value={String(childAge)}
								options={CHILDREN_AGE_RANGE_OPTIONS}
								inverted
								onUpdate={(value) => handleChildrenAgeUpdate(value, index)}
							/>
						</WithLabel>
					))}
				</>
			)}

			{!roomConf.isValid && (
				<Typography style="p" element="p" className="mt-16 text-danger">
					{translate('room-config-no-room-for-this-person-count')}
				</Typography>
			)}
		</article>
	)
}
